import { observer } from 'mobx-react-lite';
import React, { useRef } from 'react';

import Scroller from 'common/Scroller';
import Assigment from 'pages/RideDetails/components/Assigment';
import AssignAll from 'pages/RideDetails/components/AssignAll';
import BatchSegmentController from 'pages/RideDetails/components/BatchSegmentController';
import { ExtraActivities } from 'pages/RideDetails/components/ExtraActivities';
import Station from 'pages/RideDetails/components/Station';
import UnBatchSegmentController from 'pages/RideDetails/components/UnBatchSegmentController';
import VehicleAssigned from 'pages/RideDetails/components/VehicleAssigned';
import { rideDetailsStore } from 'pages/RideDetails/RideDetailsStore';

import { DATA_GRID_TEXT } from './texts';
import {
	getDayEntriesFromStationsInTimezone,
	caclRegularColumnPlacement,
	caclStationColumnPlacement,
	getGridTemplateColumns,
	getAssignmentExtraActivityLaneStacks,
	BASE_GRID_COLUMN_WIDTH,
} from './utils';

import styles from './styles.module.css';

const DataGrid = observer(() => {
	const containerRef = useRef(null);

	const {
		stations,
		vehicleAssignments,
		workAssignments,
		selectedTimezone,
		createBatchControls,
		createUnbatchControls,
	} = rideDetailsStore;

	const lastStationIndex = stations.length - 1;
	const gridTemplateColumns = getGridTemplateColumns(stations.length);
	const batchControls = createBatchControls();
	const unbatchControls = createUnbatchControls();
	const days = getDayEntriesFromStationsInTimezone(stations, selectedTimezone.value);

	// Segments touching a virtual (breakdown) station are read-only:
	// the crew there is already fixed by the breakdown flow, so any
	// status change or extra-activity insertion would corrupt it.
	const virtualStationIds = new Set(
		stations.filter((station) => station.isVirtual).map((station) => station.id)
	);
	const isBreakdownAdjacent = (assignment) =>
		virtualStationIds.has(assignment.fromStationId) ||
		virtualStationIds.has(assignment.toStationId);

	return (
		<Scroller
			scrollStep={BASE_GRID_COLUMN_WIDTH * 2}
			showScrollbar
			showControlsAlways
			styles={{
				navLeft: { width: 36, height: 36 },
				navRight: { width: 36, height: 36 },
				wrap: { padding: '0 var(--rd-side-gutter, 52px)' },
			}}
			labels={{
				hintLeft: DATA_GRID_TEXT.scrollHintLeft,
				hintRight: DATA_GRID_TEXT.scrollHintRight,
			}}
		>
			<div
				data-testid={DATA_GRID_TEXT.gridTestId}
				className={styles.dataGrid}
				style={{ gridTemplateColumns }}
			>
				<div className={styles.dayscaleWrapper}>
					{days.map((day) => (
						<div
							className={styles.daySegment}
							key={`day-${day.date}`}
							style={{ gridColumn: `${day.fromColumn} / ${day.toColumn}` }}
						>
							<div className={styles.date}>{day.date}</div>
						</div>
					))}
				</div>

				<div className={styles.frame} />

				{stations.map((station, index) => {
					const columnPlacement = caclStationColumnPlacement(index, lastStationIndex);
					return (
						<div
							key={station.id}
							className={styles.stationWrapper}
							style={{ gridColumn: `${columnPlacement.from}/${columnPlacement.to}` }}
							data-first={index === 0}
							data-last={index === lastStationIndex}
						>
							<Station station={station} />
						</div>
					);
				})}

				<div className={styles.assignAllWrapper}>
					<AssignAll />
				</div>

				{vehicleAssignments.map((vehicleAssignment) => {
					const { fromStationId, toStationId } = vehicleAssignment;
					const stationFrom = rideDetailsStore.getStationById(fromStationId);
					const stationTo = rideDetailsStore.getStationById(toStationId);
					if (stationTo === undefined || stationFrom === undefined) return null;
					const indexFrom = stationFrom.index;
					const indexTo = stationTo.index;
					const placement = caclRegularColumnPlacement(indexFrom, indexTo);

					return (
						<div
							key={`vehicle${fromStationId}${toStationId}`}
							className={styles.vehicleWrapper}
							style={{ gridColumn: `${placement.from}/${placement.to}` }}
						>
							<VehicleAssigned vehicleAssignment={vehicleAssignment} />
						</div>
					);
				})}

				{Array.from(workAssignments.values()).map((assignment, index) => {
					const { fromStationId, toStationId } = assignment;
					const stationFrom = rideDetailsStore.getStationById(fromStationId);
					const stationTo = rideDetailsStore.getStationById(toStationId);
					if (stationTo === undefined || stationFrom === undefined) return null;

					const indexFrom = stationFrom.index;
					const indexTo = stationTo.index;
					const locations = (assignment.stations ?? [])
						.map((stationId) => rideDetailsStore.getStationById(stationId))
						.filter(Boolean);

					// WA / EA container placement (between-stations coordinate system)
					const placementHorizontal = caclRegularColumnPlacement(indexFrom, indexTo);
					const laneStacks = getAssignmentExtraActivityLaneStacks({
						assignment,
						getStationById: rideDetailsStore.getStationById,
						lastStationIndex,
						indexFrom,
						indexTo,
						stationTo,
					});

					const breakdownAdjacent = isBreakdownAdjacent(assignment);

					return (
						<React.Fragment key={assignment.storeId}>
							<div
								className={styles.assignmentWrapper}
								style={{ gridColumn: `${placementHorizontal.from}/${placementHorizontal.to}` }}
							>
								<Assigment
									index={index}
									storeId={assignment.storeId}
									container={containerRef}
									isBreakdownAdjacent={breakdownAdjacent}
								/>
							</div>

							<ExtraActivities
								placementVertical={{ from: 6, to: 7 }}
								placementHorizontal={placementHorizontal}
								laneStacks={laneStacks}
								locations={locations}
								rideId={assignment.rideId ?? rideDetailsStore.rideId}
								sequenceGroupId={assignment.sequenceGroupId}
								assignment={assignment}
								canManageExtraActivities={Boolean(assignment.sequenceGroupId) && !breakdownAdjacent}
							/>
						</React.Fragment>
					);
				})}

				{batchControls.map((control) => {
					const { stationId } = control;
					const stationIndex = rideDetailsStore.getStationById(stationId).index;
					const columnPlacement = caclStationColumnPlacement(stationIndex, lastStationIndex);

					return (
						<div
							className={styles.batchControlWrapper}
							style={{ gridColumn: `${columnPlacement.from}/${columnPlacement.to}` }}
							key={`batch-control-${stationId}`}
						>
							<BatchSegmentController batchData={control} />
						</div>
					);
				})}

				{unbatchControls.map((control) => {
					const { stationId } = control;
					const stationIndex = rideDetailsStore.getStationById(stationId).index;
					const columnPlacement = caclStationColumnPlacement(stationIndex, lastStationIndex);

					return (
						<div
							className={styles.unbatchControlWrapper}
							style={{ gridColumn: `${columnPlacement.from}/${columnPlacement.to}` }}
							key={`unbatch-control-${stationId}`}
						>
							<UnBatchSegmentController breakBatchData={control} />
						</div>
					);
				})}
			</div>
		</Scroller>
	);
});

export default DataGrid;
